import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/solid';
import { motion } from 'framer-motion';
import { omit } from 'lodash';
import querystring from 'querystring';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import CountUp from 'react-countup';
import { useHistory } from 'react-router';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import { useGetProductJourneySummaryQuery } from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';
import { JourneyContext } from './JourneyContext';

interface ModalProps {
  loading: boolean;
}

export const LoadingModal = ({ loading }: ModalProps) => {
  const [open, setOpen] = useState(false);
  const barRef = useRef(null);

  const { handleClearSelection } = useContext(JourneyContext);

  const history = useHistory();

  const handleForceCloseModal = () => {
    handleClearSelection();
    setOpen(false);
    history.push({
      search: querystring.stringify(
        omit(
          {
            ...query,
          },
          ['first_product_id', 'second_product_id', 'third_product_id']
        )
      ),
    });
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        handleForceCloseModal();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (loading) {
      setOpen(true);
      return;
    }
    if (!!!loading) {
      setOpen(false);
    }
  }, [loading]);

  const { query, rawQueryParams } = useParseURLQueryParams();

  const queryParams = querystring.stringify(
    omit(
      {
        ...rawQueryParams,
      },
      ['first_product_id', 'second_product_id', 'third_product_id']
    )
  );

  const {
    output: { filtersApiPayload },
  } = useAppSelector(composableFiltersState);

  const { data: journeySummary } = useGetProductJourneySummaryQuery(
    { serializedParams: queryParams, body: filtersApiPayload },
    {
      skip: !!!query.store_name || !!!query.start_date || !!!query.end_date,
    }
  );

  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as="div"
        id="modal"
        className="fixed inset-0 z-10 overflow-y-auto"
        initialFocus={barRef}
        open={open}
        onClose={handleForceCloseModal}
      >
        <div className="min-h-screen px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100 "
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="inline-block w-full max-w-md align-middle transition-all transform bg-white shadow-xl rounded-2xl focus:outline-none">
              <div className="flex justify-end pt-4 pr-4">
                <button onClick={handleForceCloseModal}>
                  <XIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="px-12 pt-6 pb-10">
                <p className="text-center">
                  Please wait while we crunch the numbers on{' '}
                  <span className="font-bold" tabIndex={0}>
                    {journeySummary?.combination_ceiling.toLocaleString() ||
                      '...'}{' '}
                  </span>
                  product journey combinations...
                </p>

                <div className="relative h-1 mt-5">
                  <div className="absolute w-full h-full bg-gray-200" />
                  <motion.div
                    ref={barRef}
                    id="bar"
                    initial={{ width: '0' }}
                    animate={{ width: '98%' }}
                    transition={{ duration: 20 }}
                    className="relative h-full font-bold bg-segments-accent-color-1"
                  />
                </div>
                <p className="mt-2 text-center text-segments-accent-color-1">
                  <CountUp
                    duration={20}
                    start={0}
                    end={98}
                    useEasing={false}
                    formattingFn={(value) => `${value}%`}
                  />
                </p>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};
